import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SiteVisitDto, SiteVisitListDto } from '@lib/dto/site-visit.dto'
import type { VisitKind } from '@lib/enums/visit-kind.enum'
import { isRevisit, type SiteRecordKind } from '@lib/helpers/site-record'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import { toErrorMessage } from '@/helpers/logger'
import {
  acknowledge,
  currentVisit,
  fromServer,
  loadVisits,
  mergeKnown,
  previousVisit,
  saveVisits,
  visitsOf,
  startVisit,
  todayLocal,
  type LocalVisit,
  type VisitCovers,
} from '@/site-visits/visitStore'

/** How often to try again while a visit started here has not been sent. */
const RETRY_MS = 20_000

export interface SiteVisits {
  /** The visit a survey of this kind works in: the newest covering it. */
  current: (kind: VisitKind) => LocalVisit | null
  /** Every visit covering this kind of work, newest first. */
  all: (kind: VisitKind) => LocalVisit[]
  /**
   * Whether every visit started here has reached the server. The surveys
   * send nothing until it has: a change naming a visit the server has never
   * heard of is refused, and a refused batch is refused on every retry.
   */
  isSent: boolean
  /** Why the last try to send a visit failed, or null. */
  sendError: string | null
  /** Start a visit today, or widen today's; works offline. */
  start: (kinds: VisitCovers) => LocalVisit
  /** Mark a visit done, or not. Needs the network: it is the server's record. */
  setFinished: (visitId: string, finished: boolean) => Promise<void>
  /**
   * Whether a change to this part of the site record, made in this visit
   * (null: in none, so today), is made on a revisit, and so asks "Was it
   * wrong, or did it change?". The server reads an unanswered one by the
   * same rule.
   */
  isRevisit: (kind: SiteRecordKind, visitId: string | null) => boolean
  /**
   * The visit before this one that looked at this kind of work, whose
   * findings a revisit shows as "Last visit"; null for a first visit.
   */
  previous: (kind: VisitKind, visit: LocalVisit | null) => LocalVisit | null
}

/** The server's list with one visit it answered with put in its place. */
function withVisit(
  list: readonly SiteVisitDto[],
  answer: SiteVisitDto,
): SiteVisitDto[] {
  return [...list.filter((visit) => visit.id !== answer.id), answer]
}

/**
 * A site's visits, as both survey screens share them. See docs/site-visits.md.
 *
 * Two parts, and only the second is this phone's state: the server's list
 * (from the query, or the copy stored with the visits when there is no
 * signal), and the visits started or widened here that the server has not
 * acknowledged. The visits shown are derived from the two on each render, so
 * there is no moment where they disagree. An answer from the server goes
 * straight into the query's copy, so a visit just acknowledged is never
 * briefly missing between the answer and the next read of the list.
 *
 * Mount one per site, keyed by the site id.
 */
export function useSiteVisits(siteId: string): SiteVisits {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: siteVisitKeys.list(siteId),
    queryFn: () => siteVisitService.list(siteId),
    enabled: siteId !== '',
    retry: false,
    staleTime: 30_000,
  })
  const [stored] = React.useState(() =>
    siteId === '' ? [] : loadVisits(siteId).visits,
  )
  const [pending, setPending] = React.useState<LocalVisit[]>(() =>
    stored.filter((visit) => visit.pending),
  )
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const sendingRef = React.useRef(false)

  /*
   * What the server last said: the query's answer, or, before one arrives,
   * the copy stored on the phone (which a cold start with no signal has).
   */
  const known = React.useMemo<readonly LocalVisit[]>(
    () =>
      query.data?.visits.map(fromServer) ??
      stored.filter((visit) => !visit.pending),
    [query.data, stored],
  )
  const state = React.useMemo(
    () => mergeKnown({ visits: pending }, known),
    [pending, known],
  )
  const stateRef = React.useRef(state)
  React.useLayoutEffect(() => {
    stateRef.current = state
  })

  /* -- persist ------------------------------------------------------- */

  React.useEffect(() => {
    if (siteId !== '') saveVisits(siteId, state)
  }, [siteId, state])

  React.useEffect(() => {
    function goOnline(): void {
      setIsOnline(true)
    }
    function goOffline(): void {
      setIsOnline(false)
    }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  /* -- the server's answers ------------------------------------------ */

  /**
   * Take one answer: into the query's copy of the list, and out of what is
   * owed (unless the visit was widened again while the answer was coming).
   */
  const take = React.useCallback(
    (sentId: string, answer: SiteVisitDto): void => {
      queryClient.setQueryData<SiteVisitListDto>(
        siteVisitKeys.list(siteId),
        (old) => ({ siteId, visits: withVisit(old?.visits ?? [], answer) }),
      )
      setPending((owed) =>
        acknowledge({ visits: owed }, sentId, answer).visits.filter(
          (visit) => visit.pending,
        ),
      )
    },
    [queryClient, siteId],
  )

  /* -- send what was started here ----------------------------------- */

  const send = React.useCallback((): void => {
    if (siteId === '' || sendingRef.current || !navigator.onLine) return
    const owed = stateRef.current.visits.filter((visit) => visit.pending)
    if (owed.length === 0) return
    sendingRef.current = true
    void (async (): Promise<void> => {
      try {
        for (const visit of owed) {
          const answer = await siteVisitService.start(siteId, {
            id: visit.id,
            startedOn: visit.startedOn,
            coversTrees: visit.coversTrees,
            coversIrrigation: visit.coversIrrigation,
          })
          take(visit.id, answer)
        }
        setSendError(null)
      } catch (caught) {
        setSendError(toErrorMessage(caught))
      } finally {
        sendingRef.current = false
      }
    })()
  }, [siteId, take])

  const pendingCount = pending.length

  React.useEffect(() => {
    if (pendingCount === 0) return undefined
    send()
    const timer = window.setInterval(send, RETRY_MS)
    return () => {
      window.clearInterval(timer)
    }
  }, [pendingCount, isOnline, send])

  /* -- actions -------------------------------------------------------- */

  const start = React.useCallback(
    (kinds: VisitCovers): LocalVisit => {
      const result = startVisit(stateRef.current, siteId, kinds, todayLocal())
      // Read straight back by a survey that records a finding next.
      stateRef.current = result.state
      setPending(result.state.visits.filter((visit) => visit.pending))
      return result.visit
    },
    [siteId],
  )

  const setFinished = React.useCallback(
    async (visitId: string, finished: boolean): Promise<void> => {
      take(visitId, await siteVisitService.setFinished(visitId, finished))
    },
    [take],
  )

  return React.useMemo(
    () => ({
      current: (kind: VisitKind) => currentVisit(state, kind),
      all: (kind: VisitKind) => visitsOf(state, kind),
      isSent: pendingCount === 0,
      sendError,
      start,
      setFinished,
      isRevisit: (kind: SiteRecordKind, visitId: string | null) => {
        const since =
          state.visits.find((visit) => visit.id === visitId)?.startedOn ??
          todayLocal()
        return isRevisit(state.visits, kind, since)
      },
      previous: (kind: VisitKind, visit: LocalVisit | null) =>
        visit === null ? null : previousVisit(state, kind, visit),
    }),
    [state, pendingCount, sendError, start, setFinished],
  )
}
